import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { Expense } from "@/lib/domains";
import { RecurringExpenseService } from "@/lib/domains/recurring";
import { DEFAULT_BASE_CURRENCY } from "@/lib/utils/currency";
import {
    getSupabase,
    getExpenses,
    addExpense as createExpense,
    updateExpense as modifyExpense,
    deleteExpense as removeExpense,
    getCurrentUser,
    getUserSettings as fetchUserSettings,
    updateUserSettings as saveUserSettings,
} from "@/lib/database";
import type { User } from "@supabase/supabase-js";

interface ExpenseContextType {
    expenses: Expense[];
    addExpense: (expense: Omit<Expense, "id" | "createdAt" | "userId">) => Promise<void>;
    updateExpense: (id: string, updates: Partial<Omit<Expense, "id" | "userId" | "createdAt">>) => Promise<void>;
    deleteExpense: (id: string) => Promise<void>;
    currentUser: { id: string; name: string; email?: string } | null;
    loading: boolean;
    error: string | null;
    refreshExpenses: () => Promise<void>;
    defaultCurrency: string;
    updateDefaultCurrency: (currency: string) => Promise<void>;
}

const ExpenseContext = createContext<ExpenseContextType | undefined>(undefined);

/**
 * Format Supabase user to our user object
 */
function formatUser(user: User | null): { id: string; name: string; email?: string } | null {
    if (!user) return null;
    return {
        id: user.id,
        name: user.user_metadata?.full_name || user.email || "User",
        email: user.email,
    };
}

export function ExpenseProvider({ children }: { children: React.ReactNode }) {
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [currentUser, setCurrentUser] = useState<{ id: string; name: string; email?: string } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [defaultCurrency, setDefaultCurrency] = useState<string>(DEFAULT_BASE_CURRENCY);

    // Load initial user and expenses
    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            try {
                const user = await getCurrentUser();
                if (!isMounted) return;

                setCurrentUser(formatUser(user));

                if (user) {
                    const [expensesData, settingsData] = await Promise.all([
                        getExpenses(),
                        fetchUserSettings(),
                    ]);
                    if (isMounted) {
                        setExpenses(expensesData);
                        setDefaultCurrency(settingsData?.defaultCurrency || DEFAULT_BASE_CURRENCY);

                        // Auto-generate due recurring expenses
                        try {
                            const generated = await RecurringExpenseService.generateDueExpenses();
                            if (generated > 0) {
                                // Refresh expenses to show auto-generated ones
                                const updatedExpenses = await getExpenses();
                                setExpenses(updatedExpenses);
                            }
                        } catch (err) {
                            console.error('Failed to auto-generate recurring expenses:', err);
                        }
                    }
                } else {
                    setExpenses([]);
                }
            } catch (err) {
                if (isMounted) {
                    setError(err instanceof Error ? err.message : "Failed to load data");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadData();

        // Subscribe to auth changes
        const supabase = getSupabase();
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
            if (!isMounted) return;

            const user = formatUser(session?.user ?? null);
            setCurrentUser(user);

            if (user) {
                // User signed in - load their expenses and settings
                setLoading(true);
                try {
                    const [expensesData, settingsData] = await Promise.all([
                        getExpenses(),
                        fetchUserSettings(),
                    ]);
                    if (isMounted) {
                        setExpenses(expensesData);
                        setDefaultCurrency(settingsData?.defaultCurrency || DEFAULT_BASE_CURRENCY);

                        // Auto-generate due recurring expenses
                        try {
                            const generated = await RecurringExpenseService.generateDueExpenses();
                            if (generated > 0) {
                                // Refresh expenses to show auto-generated ones
                                const updatedExpenses = await getExpenses();
                                setExpenses(updatedExpenses);
                            }
                        } catch (err) {
                            console.error('Failed to auto-generate recurring expenses:', err);
                        }
                    }
                } catch (err) {
                    if (isMounted) {
                        setError(err instanceof Error ? err.message : "Failed to load expenses");
                    }
                } finally {
                    if (isMounted) {
                        setLoading(false);
                    }
                }
            } else {
                // User signed out - clear expenses and reset currency
                setExpenses([]);
                setDefaultCurrency(DEFAULT_BASE_CURRENCY);
                setLoading(false);
            }
        });

        return () => {
            isMounted = false;
            subscription.unsubscribe();
        };
    }, []);

    const refreshExpenses = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getExpenses();
            setExpenses(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to fetch expenses");
        } finally {
            setLoading(false);
        }
    }, []);

    const addExpense = useCallback(async (
        expense: Omit<Expense, "id" | "createdAt" | "userId">
    ): Promise<void> => {
        setError(null);
        try {
            const newExpense = await createExpense(expense);
            setExpenses((prev) => [newExpense, ...prev]);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to add expense");
            throw err;
        }
    }, []);

    const updateExpense = useCallback(async (
        id: string,
        updates: Partial<Omit<Expense, "id" | "userId" | "createdAt">>
    ): Promise<void> => {
        setError(null);
        try {
            const updatedExpense = await modifyExpense(id, updates);
            setExpenses((prev) =>
                prev.map((exp) => (exp.id === id ? updatedExpense : exp))
            );
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update expense");
            throw err;
        }
    }, []);

    const deleteExpense = useCallback(async (id: string): Promise<void> => {
        setError(null);
        try {
            await removeExpense(id);
            setExpenses((prev) => prev.filter((exp) => exp.id !== id));
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to delete expense");
            throw err;
        }
    }, []);

    const updateDefaultCurrency = useCallback(async (currency: string): Promise<void> => {
        setError(null);
        try {
            const updatedSettings = await saveUserSettings({ defaultCurrency: currency });
            setDefaultCurrency(updatedSettings.defaultCurrency);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update default currency");
            throw err;
        }
    }, []);

    const value: ExpenseContextType = {
        expenses,
        addExpense,
        updateExpense,
        deleteExpense,
        currentUser,
        loading,
        error,
        refreshExpenses,
        defaultCurrency,
        updateDefaultCurrency,
    };

    return React.createElement(ExpenseContext.Provider, { value }, children);
}

export function useExpenses(): ExpenseContextType {
    const context = useContext(ExpenseContext);
    if (!context) {
        throw new Error("useExpenses must be used within ExpenseProvider");
    }
    return context;
}
